<template>
	<div class="heat-loss-calc">
		<div class="step">
			<h2>Select your calculator</h2>
			<div class="fields">
				<div class="field">
					<select v-model="activeCalculator">
						<option v-for="calculator, index in calculators" :key="index" :value="calculator.handle">{{ calculator.label }}</option>
					</select>
				</div>
				<div class="field">
					<select v-model="activeUnit">
						<option v-for="unit, index in units" :key="index" :value="unit.handle">{{ unit.label }}</option>
					</select>
				</div>
			</div>
		</div>
		<div v-if="activeCalculator == 'room'" class="calculator">
			<div class="step">
				<h2>Floors</h2>
				<div class="fields">
					<div class="field" :class="{'error': !floorLevelIsValid && room.floorsInvalid}">
						<select v-model="room.floorLevel">
							<option disabled value="">Floor level of room</option>
							<option v-for="floor, index in floorTypes" :key="index" :value="floor.handle">{{ floor.label }}</option>
						</select>
					</div>
					<div class="field" :class="{'error': !roomHeightIsValid && room.floorsInvalid}">
						<select v-model="room.roomHeight">
							<option disabled value="">Room height</option>
							<option v-for="height, index in possibleRoomHeights" :key="index" :value="height.normalisedValue">{{ height.value }}{{ lengthSymbol }}</option>
						</select>
					</div>
					<div class="field" :class="{'error': !floorAreaIsValid && room.floorsInvalid}">
						<input v-model="room.floorArea" @keypress="allowNumbers" placeholder="Floor area. 0"><span v-html="areaSymbol"></span>
					</div>
				</div>
				<ul class="errors" v-show="room.floorsInvalid">
					<li v-if="!floorLevelIsValid">Please enter a floor level. Floor level of room must be selected.</li>
					<li v-if="!roomHeightIsValid">Please enter a room height. Room height must be selected.</li>
					<li v-if="!floorAreaIsValid">Please enter the floor area. Floor area must be set and not equal to 0.</li>
				</ul>
			</div>
			<div class="step">
				<h2>External Walls & Windows</h2>
				<div class="wall" v-for="wall, index in room.externalWalls" :key="index">
					<div class="fields">
						<div class="field">
							<select v-model="room.externalWalls[index].wallType">
								<option v-for="wallOption, index in wallTypes" :key="index" :value="wallOption.value" :selected="wallOption.value == wall.value">{{ wallOption.label }}</option>
							</select>
						</div>
						<div class="field">
							<input @keypress="allowNumbers" v-model="room.externalWalls[index].wallLength"><span v-html="lengthSymbol"></span>
						</div>
						<div class="field">
							<select v-model="room.externalWalls[index].windowType" @change="senseCheckExternalWallsWindowArea(index)">
								<option v-for="windowOption, index in windowTypes" :key="index" :value="windowOption.value" :selected="windowOption.value == wall.value">{{ windowOption.label }}</option>
							</select>
						</div>
						<div class="field">
							<input @keypress="allowNumbers" v-model="room.externalWalls[index].windowArea" :disabled="room.externalWalls[index].windowType === '2'"><span v-html="areaSymbol"></span>
						</div>
						<button @click="removeExternalWallToRoom(index)">Remove Wall</button>
					</div>
				</div>
				<div>
					<p>Add a new wall to the calculation</p>
					<div class="fields">
						<div class="field" :class="{'error' : (!wallTypeIsValid) && room.externalWallInvalid}">
							<select v-model="room.externalWall.wallType">
								<option disabled value="">Outside wall type</option>
								<option v-for="wallOption, index in wallTypes" :key="index" :value="wallOption.value">{{ wallOption.label }}</option>
							</select>
						</div>
						<div class="field" :class="{'error' : (!wallLengthIsValid) && room.externalWallInvalid}">
							<input @keypress="allowNumbers" v-model="room.externalWall.wallLength" :placeholder="'Length of outside wall. 0'"><span v-html="lengthSymbol"></span>
						</div>
						<div class="field" :class="{'error' : (!windowTypeIsValid) && room.externalWallInvalid}">
							<select v-model="room.externalWall.windowType" @change="senseCheckExternalWallWindowArea">
								<option disabled value="">Window type</option>
								<option v-for="windowOption, index in windowTypes" :key="index" :value="windowOption.value">{{ windowOption.label }}</option>
							</select>
						</div>
						<div class="field" :class="{'error' : (!windowAreaIsValid) && room.externalWallInvalid}">
							<input @keypress="allowNumbers" v-model="room.externalWall.windowArea" :placeholder="'Area of window. 0'" :disabled="room.externalWall.windowType === '2'"><span v-html="areaSymbol"></span>
						</div>
						<button @click="addExternalWallToRoom">Add Wall</button>
					</div>
				</div>
				<ul class="errors" v-show="room.externalWallInvalid">
					<li v-if="!wallTypeIsValid">Please enter a wall type. The type of wall must be selected.</li>
					<li v-if="!wallLengthIsValid">Please enter a wall length. Wall length must be set and not equal to 0.</li>
					<li v-if="!windowTypeIsValid">Please enter a window type. The type of window must be selected. If the wall has no window, please select "No Window"</li>
					<li v-if="!windowAreaIsValid">Please enter a window area. Window area must be set and not equal to 0.</li>
				</ul>
				<ul class="errors" v-show="room.externalWallsInvalid">
					<li>Please enter a wall. At least one wall must be set.</li>
				</ul>
			</div>
			<div class="step">
				<h2>Variation</h2>
				<div class="fields checkboxes">
					<div class="field">
						<label class="checkbox-label" for="coolerRoom"><span>Cooler room</span>
							<input type="checkbox" v-model="room.variation.coolerRoom" id="coolerRoom">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="exposedSituations"><span>Exposed situations</span>
							<input type="checkbox" v-model="room.variation.exposedSituations" id="exposedSituations">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="internalRoomWithFanExtractor"><span>Internal room with fan extractor</span>
							<input type="checkbox" v-model="room.variation.internalRoomWithFanExtractor" id="internalRoomWithFanExtractor">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="allWindowWalls"><span>All window walls</span>
							<input type="checkbox" v-model="room.variation.allWindowWalls" id="allWindowWalls">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="singleStoryBuilding"><span>Single story building</span>
							<input type="checkbox" v-model="room.variation.singleStoryBuilding" id="singleStoryBuilding">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="ceilingWithoutInsulationOnTopFloorsAndSingleStoryBuildings"><span>Ceiling without insulation on top floors and single story buildings</span>
							<input type="checkbox" v-model="room.variation.ceilingWithoutInsulationOnTopFloorsAndSingleStoryBuildings" id="ceilingWithoutInsulationOnTopFloorsAndSingleStoryBuildings">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="floorWithInsulation"><span>Floor with insulation</span>
							<input type="checkbox" v-model="room.variation.floorWithInsulation" id="floorWithInsulation">
						</label>
					</div>
					<div class="field">
						<label class="checkbox-label" for="veluxWindowsOrSkylights"><span>Velux windows or skylights</span>
							<input type="checkbox" v-model="room.variation.veluxWindowsOrSkylights" id="veluxWindowsOrSkylights">
						</label>
					</div>
				</div>
			</div>
			<div class="step" v-if="this.room.variation.veluxWindowsOrSkylights">
				<h2>Velux / Skylight Options</h2>
				<div class="skylight" v-for="skylight, index in room.skylights" :key="index">
					<div class="fields">
						<div class="field">
							<select v-model="room.skylights[index].skylightType">
								<option v-for="skylightOption, index in windowTypes" :key="index" :disabled="skylightOption.value == '2'" :value="skylightOption.value" :selected="skylightOption.value == skylight.value">{{ skylightOption.label }}</option>
							</select>
						</div>
						<div class="field">
							<input @keypress="allowNumbers" v-model="room.skylights[index].skylightArea"><span v-html="areaSymbol"></span>
						</div>
						<button class="remove" @click="removeSkylightToRoom(index)">Remove Skylight</button>
					</div>
				</div>
				<p>Add a new skylight to the calculation</p>
				<div class="fields">
					<div class="field" :class="{'error' : !skylightTypeIsValid && room.skylightInvalid}">
						<select v-model="room.skylight.skylightType">
							<option disabled value="">Window type</option>
							<option v-for="skylightOption, index in windowTypes" :key="index" :disabled="skylightOption.value == '2'" :value="skylightOption.value">{{ skylightOption.label }}</option>
						</select>
					</div>
					<div class="field" :class="{'error' : !skylightAreaIsValid && room.skylightInvalid}">
						<input @keypress="allowNumbers" v-model="room.skylight.skylightArea" :placeholder="'Area of skylight. 0'"><span v-html="lengthSymbol"></span>
					</div>
					<button class="add" @click="addSkylightToRoom">Add Skylight</button>
				</div>
				<ul class="errors" v-show="room.skylightInvalid">
					<li v-if="!skylightTypeIsValid">Please enter a skylight type. The type of skylight must be selected.</li>
					<li v-if="!skylightAreaIsValid">Please enter the area skylight. Skylight area must be set and not equal to 0.</li>
				</ul>
				<ul class="errors" v-show="room.skylightsInvalid">
					<li>Please enter a skylight. At least one skylight must be set if opting for the "Velux windows or skylights" variation.</li>
				</ul>
			</div>
			<div class="step">
				<h2>Total</h2>
				<button class="calculate" @click="roomCalculate">Calculate</button>
				<p class="total">Your room requires an output of... <span class="field inline"><input disabled :value="room.lastCalculatedValue"><span>&nbsp;WATTS</span></span></p>
			</div>
		</div>
		<div v-if="activeCalculator == 'conservatory'" class="calculator">
			<div class="step">
				<h2>Type</h2>
				<div class="fields">
					<div class="field" :class="{'error' : !conservatoryTypeIsValid && conservatory.conservatoryInvalid}">
						<select v-model="conservatory.type">
							<option selected="true" value="">Glazing</option>
							<option v-for="glazing, index in conservatoryTypes" :key="index" :value="glazing.value">{{ glazing.label }}</option>
						</select>
					</div>
					<div class="field" :class="{'error' : !conservatoryHeightIsValid && conservatory.conservatoryInvalid}">
						<select v-model="conservatory.roomHeight">
							<option selected="true" value="">Room Height</option>
							<option v-for="height, index in possibleConservatoryHeights" :key="index" :value="height.value">{{ height.label }}</option>
						</select>
					</div>
					<div class="field" :class="{'error' : !conservatoryAreaIsValid && conservatory.conservatoryInvalid}">
						<input v-model="conservatory.floorArea" :placeholder="'Floor Area. 0'" @keypress="allowNumbers"><span v-html="areaSymbol"></span>
					</div>
					<div class="field" :class="{'error' : !conservatoryWallsIsValid && conservatory.conservatoryInvalid}">
						<input v-model="conservatory.lengthOfWalls" :placeholder="'Length Of Outside Wall(s). 0'" @keypress="allowNumbers"><span v-html="lengthSymbol"></span>
					</div>
				</div>
				<ul class="errors" v-show="conservatory.conservatoryInvalid">
					<li v-if="!conservatoryTypeIsValid">Please enter a floor level. Floor level of room must be selected.</li>
					<li v-if="!conservatoryHeightIsValid">Please enter a room height. Room height must be selected.</li>
					<li v-if="!conservatoryAreaIsValid">Please enter the floor area. Floor area must be set and not equal to 0.</li>
					<li v-if="!conservatoryWallsIsValid">Please enter the floor area. Floor area must be set and not equal to 0.</li>
				</ul>
			</div>
			<div class="step">
				<h2>Variation</h2>
				<div class="fields checkboxes">
					<div class="field">
						<label class="checkbox-label" for="forBackgroundHeatOnly"><span>For background heat only</span>
							<input type="checkbox" v-model="conservatory.variation.forBackgroundHeatOnly" id="forBackgroundHeatOnly">
						</label>
					</div>
				</div>
			</div>
			<div class="step">
				<h2>Total</h2>
				<button class="calculate" @click="conservatoryCalculate">Calculate</button>
				<p class="total">Your room requires an output of... <span class="field inline"><input disabled :value="conservatory.lastCalculatedValue"><span>&nbsp;WATTS</span></span></p>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		data: function() {
			return {
				calculators: [
					{handle: "room", label: "Room"},
					{handle: "conservatory", label: "Conservatory"},
				],
				units: [
					{handle: "metric", label: "Metric"},
					{handle: "imperial", label: "Imperial"},
				],
				lengthUnitTypes: [
					{handle: "metric", label: "Meters", symbol: "m"},
					{handle: "imperial", label: "Feet", symbol: "ft"},
				],
				areaUnitTypes: [
					{handle: "metric", label: "Meters Squared", symbol: "m&sup2;"},
					{handle: "imperial", label: "Feet Squared", symbol: "ft&sup2;"},
				],
				volumeUnitTypes: [
					{handle: "metric", label: "Cubic Meters", symbol: "m&sup3;"},
					{handle: "imperial", label: "Cubic Feet", symbol: "ft&sup3;"},
				],
				floorTypes: [
					{handle: "basement", label: "Basement", value: "0"},
					{handle: "ground", label: "Ground Floor", value: "1"},
					{handle: "middle", label: "Middle Floor", value: "2"},
					{handle: "top", label: "Top Floor", value: "3"},
				],
				roomHeights: [
					{type: "metric", value: 2.4, normalisedValue: "0"},
					{type: "metric", value: 2.7, normalisedValue: "1"},
					{type: "metric", value: 3, normalisedValue: "2"},
					{type: "metric", value: 3.3, normalisedValue: "3"},
					{type: "metric", value: 3.6, normalisedValue: "4"},
					{type: "metric", value: 3.9, normalisedValue: "5"},
					{type: "metric", value: 4.2, normalisedValue: "6"},
					{type: "imperial", value: 8, normalisedValue: "0"},
					{type: "imperial", value: 9, normalisedValue: "1"},
					{type: "imperial", value: 10, normalisedValue: "2"},
					{type: "imperial", value: 11, normalisedValue: "3"},
					{type: "imperial", value: 12, normalisedValue: "4"},
					{type: "imperial", value: 13, normalisedValue: "5"},
					{type: "imperial", value: 14, normalisedValue: "6"},
				],
				wallTypes: [
					{handle: "solid", label: "Solid Wall", value: "0"},
					{handle: "cavity", label: "Cavity Wall", value: "1"},
					{handle: "insulated", label: "Insulated Wall", value: "2"},
				],
				windowTypes: [
					{handle: "no", label: "No Window", value: "2"},
					{handle: "single", label: "Single Glass", value: "0"},
					{handle: "double", label: "Double Glass", value: "1"},
				],
				conservatoryTypes: [
					{handle: "double", label: "Double Glazing", value: "0"},
					{handle: "lowE", label: "Low 'E' Glass", value: "1"},
					{handle: "lowEWithArgon", label: "Low 'E' Glass With Argon", value: "2"},
				],
				conservatoryHeights: [
					{type: "metric", label: "2.1", value: "0"},
					{type: "metric", label: "2.4", value: "1"},
					{type: "metric", label: "2.7", value: "2"},
					{type: "metric", label: "3.0", value: "3"},
					{type: "imperial", label: "7", value: "0"},
					{type: "imperial", label: "8", value: "1"},
					{type: "imperial", label: "9", value: "2"},
					{type: "imperial", label: "10", value: "3"},
				],
				activeCalculator: null,
				activeUnit: null,
				room: {
					floorLevel: "",
					roomHeight: "",
					floorArea: null,
					floorsInvalid: false,
					externalWall: {
						wallType: "",
						wallLength: null,
						windowType: "",
						windowArea: null,
					},
					externalWallInvalid: false,
					externalWalls: [],
					externalWallsInvalid: false,
					skylight: {
						skylightType: "",
						skylightArea: null,
					},
					skylightInvalid: false,
					skylights: [],
					skylightsInvalid:false,
					variation: {
						coolerRoom: false,
						exposedSituations: false,
						internalRoomWithFanExtractor: false,
						allWindowWalls: false,
						singleStoryBuilding: false,
						ceilingWithoutInsulationOnTopFloorsAndSingleStoryBuildings: false,
						floorWithInsulation: false,
						veluxWindowsOrSkylights: false,
					},
					lastCalculatedValue: null,
				},
				conservatory: {
					type: "",
					roomHeight: "",
					floorArea: null,
					lengthOfWalls: null,
					variation: {
						forBackgroundHeatOnly: false,
					},
					conservatoryInvalid: false,
					lastCalculatedValue: null,
				}
			};
		},
		methods: {
			senseCheckExternalWallWindowArea: function() {
				if (this.room.externalWall.windowType === "2") {
					this.room.externalWall.windowArea = 0;
					console.log("sense check external wall window area");
				}
			},
			senseCheckExternalWallsWindowArea: function(index) {
				if (this.room.externalWalls[index].windowType === "2") {
					this.room.externalWalls[index].windowArea = 0;
					console.log("sense check external wall window area");
					console.log("area" + this.room.externalWalls[index].windowArea);
				}
			},
			allowNumbers: function(event) {
				event = (event) ? event : window.event;
				let characterCode = (event.which) ? event.which : event.keyCode;
				if ((characterCode > 31 && (characterCode < 48 || characterCode > 57)) && characterCode !== 46) event.preventDefault();
			},
			onCalculatorSelected: function(calculator) {
				this.activeCalculator = calculator;
			},
			defaultCalculator: function() {
				return this.calculators[0] ? this.calculators[0].handle : null; 
			},
			defaultUnit: function() {
				return this.units[0] ? this.units[0].handle : null; 
			},
			setActiveCalculator: function(handle) {
				this.activeCalculator = handle;
			},
			setActiveUnit: function(handle) {
				this.activeUnit = handle;
			},
			normaliseValue: function(value) {
				if (this.activeUnit == "metric") return parseFloat(value);
				if (this.activeUnit == "imperial") return parseFloat(value)/3.28084;
				return value;
			},
			addExternalWallToRoom: function(event) {
				let valid = true;
				if (!this.wallTypeIsValid || !this.wallLengthIsValid || !this.windowTypeIsValid || !this.windowAreaIsValid) {
					this.room.externalWallInvalid = true;
					valid = false;
				}
				if (!valid) return;
				this.room.externalWallInvalid = false;
				this.room.externalWalls.push({...this.room.externalWall});
				this.room.externalWall.wallType = "";
				this.room.externalWall.wallLength = null;
				this.room.externalWall.windowType = "";
				this.room.externalWall.windowArea = null;
				this.room.externalWallsInvalid = false;
			},
			addSkylightToRoom: function(event) {
				let valid = true;
				if (!this.skylightTypeIsValid || !this.skylightAreaIsValid) {
					this.room.skylightInvalid = true;
					valid = false;
				}
				if (!valid) return;
				this.room.skylightInvalid = false;
				this.room.skylights.push({...this.room.skylight});
				this.room.skylight.skylightType = "";
				this.room.skylight.skylightArea = "";
				this.room.skylightsInvalid = false;
			},
			removeExternalWallToRoom: function(index) {
				this.room.externalWalls.splice(index, 1);
			},
			removeSkylightToRoom: function(index) {
				this.room.skylights.splice(index, 1);
			},
			roomCalculate: function(event) {
				let valid = true;
				if (!this.floorsAreValid) {
					this.room.floorsInvalid = !this.floorsAreValid;
					valid = false;
				}
				if (!this.room.externalWalls.length) {
					this.room.externalWallsInvalid = true;
					valid = false;
				}
				if (this.room.variation.veluxWindowsOrSkylights && this.room.skylights.length <= 0) {
					this.room.skylightsInvalid = true;
					valid = false;
				}
				if (!valid) return;
				this.room.lastCalculatedValue = Math.round(this.roomCalculatedValue);
			},
			conservatoryCalculate: function() {
				let valid = true;
				if (!this.conservatoryIsValid) {
					this.conservatory.conservatoryInvalid = !this.conservatoryIsValid;
					valid = false;
				}
				if (!valid) return;
				this.conservatory.lastCalculatedValue = Math.round(this.conservatoryCalculatedValue);
			},
			floorTypeValue: function(handle) {
				return this.floorTypes.filter(type => {
  				return type.handle === handle
				})[0].value;
			},
			roomHeightValue: function(handle) {
				return this.roomHeights.filter(type => {
					return type.handle === handle
				})[0].normalisedValue;
			},
			conservatoryHeightValue: function(handle) {
				return this.conservatoryHeights.filter(type => {
					return type.handle === handle
				})[0].value;
			},
			floorTotal: function(handle) {
				let values = [
					{id: "00", metric: 79, imperial: 7.2},
					{id: "01", metric: 84, imperial: 7.6},
					{id: "02", metric: 88, imperial: 8.1},
					{id: "03", metric: 93, imperial: 8.7},
					{id: "04", metric: 99, imperial: 9.2},
					{id: "05", metric: 104, imperial: 9.7},
					{id: "06", metric: 106, imperial: 10.3},
					{id: "10", metric: 79, imperial: 7.2},
					{id: "11", metric: 84, imperial: 7.6},
					{id: "12", metric: 88, imperial: 8.1},
					{id: "13", metric: 93, imperial: 8.7},
					{id: "14", metric: 99, imperial: 9.2},
					{id: "15", metric: 104, imperial: 9.7},
					{id: "16", metric: 108, imperial: 10.3},
					{id: "20", metric: 52, imperial: 4.8},
					{id: "21", metric: 57, imperial: 5.3},
					{id: "22", metric: 63, imperial: 5.7},
					{id: "23", metric: 68, imperial: 6.3},
					{id: "24", metric: 72, imperial: 6.7},
					{id: "25", metric: 77, imperial: 7.2},
					{id: "26", metric: 81, imperial: 7.7},
					{id: "30", metric: 52, imperial: 4.8},
					{id: "31", metric: 57, imperial: 5.3},
					{id: "32", metric: 63, imperial: 5.7},
					{id: "33", metric: 68, imperial: 6.3},
					{id: "34", metric: 72, imperial: 6.7},
					{id: "35", metric: 77, imperial: 7.3},
					{id: "36", metric: 81, imperial: 7.9},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
			wallTotal: function(handle) {
				let values = [
					{id: "00", metric: 124, imperial:37},
					{id: "01", metric: 140, imperial:42},
					{id: "02", metric: 155, imperial:47},
					{id: "03", metric: 171, imperial:51},
					{id: "04", metric: 186, imperial:55},
					{id: "05", metric: 201, imperial:61},
					{id: "06", metric: 216, imperial:65},
					{id: "10", metric: 89, imperial:27},
					{id: "11", metric: 100, imperial:30},
					{id: "12", metric: 111, imperial:33},
					{id: "13", metric: 122, imperial:38},
					{id: "14", metric: 133, imperial:41},
					{id: "15", metric: 144, imperial:44},
					{id: "16", metric: 155, imperial:48},
					{id: "20", metric: 30, imperial:9},
					{id: "21", metric: 33, imperial:10},
					{id: "22", metric: 37, imperial:11},
					{id: "23", metric: 41, imperial:12},
					{id: "24", metric: 44, imperial:13},
					{id: "25", metric: 47, imperial:14},
					{id: "26", metric: 50, imperial:15},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
			windowTotal: function(handle) {
				let values = [
					{id: "00", metric: 72, imperial: 6.4},
					{id: "01", metric: 86, imperial: 7.7},
					{id: "02", metric: 111, imperial: 10.0},
					{id: "10", metric: 10, imperial: 0.9},
					{id: "11", metric: 25, imperial: 2.2},
					{id: "12", metric: 48, imperial: 4.4},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
			allWindowTotal: function(handle) {
				let values = [
					{id: "0", metric: 138, imperial: 12.5},
					{id: "1", metric: 74, imperial: 6.5},
					{id: "2", metric: 0, imperial: 0},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
			skylightTotal: function(handle) {
				let values = [
					{id: "0", metric: 158, imperial: 2.5},
					{id: "1", metric: 58, imperial: 8.0},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
			conservatoryFloorTotal: function(handle) {
				let values = [
					{id: "00", metric: 113, imperial: 10.2},
					{id: "01", metric: 116, imperial: 10.4},
					{id: "02", metric: 119, imperial: 10.6},
					{id: "03", metric: 122, imperial: 10.8},
					{id: "10", metric: 90, imperial: 8.2},
					{id: "11", metric: 93, imperial: 8.4},
					{id: "12", metric: 96, imperial: 8.6},
					{id: "13", metric: 99, imperial: 8.8},
					{id: "20", metric: 76, imperial: 6.9},
					{id: "21", metric: 79, imperial: 7.1},
					{id: "22", metric: 82, imperial: 7.3},
					{id: "23", metric: 85, imperial: 7.5},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
			conservatoryWallTotal: function(handle) {
				let values = [
					{id: "00", metric: 130, imperial: 39},
					{id: "01", metric: 150, imperial: 45},
					{id: "02", metric: 170, imperial: 51},
					{id: "03", metric: 190, imperial: 57},
					{id: "10", metric: 98, imperial: 30},
					{id: "11", metric: 113, imperial: 34},
					{id: "12", metric: 128, imperial: 38},
					{id: "13", metric: 143, imperial: 42},
					{id: "20", metric: 78, imperial: 23},
					{id: "21", metric: 89, imperial: 26},
					{id: "22", metric: 100, imperial: 29},
					{id: "23", metric: 111, imperial: 32},
				];
				return values.filter(value => {
					return value.id === handle;
				})[0];
			},
		},
		computed: {
			conservatoryIsValid: function() {
				if (!this.conservatoryTypeIsValid) return false;
				if (!this.conservatoryHeightIsValid) return false;
				if (!this.conservatoryAreaIsValid) return false;
				if (!this.conservatoryWallsIsValid) return false;
				return true;
			},
			conservatoryWallsIsValid: function() {
				return this.conservatory.lengthOfWalls === null || this.conservatory.lengthOfWalls === "" || parseFloat(this.conservatory.lengthOfWalls) <= 0 ? false : true;
			},
			conservatoryAreaIsValid: function() {
				return this.conservatory.floorArea === null || this.conservatory.floorArea === "" || parseFloat(this.conservatory.floorArea) <= 0 ? false : true;
			},
			conservatoryHeightIsValid: function() {
				return this.conservatory.roomHeight === null || this.conservatory.roomHeight === "" ? false : true;
			},
			conservatoryTypeIsValid: function() {
				return this.conservatory.type === null || this.conservatory.type === "" ? false : true;
			},
			skylightAreaIsValid: function() {
				return this.room.skylight.skylightArea === null || this.room.skylight.skylightArea === "" || parseFloat(this.room.skylight.skylightArea) <= 0 ? false : true;
			},
			skylightTypeIsValid: function() {
				return this.room.skylight.skylightType === null || this.room.skylight.skylightType === "" ? false : true;
			},
			windowAreaIsValid: function() {
				if (this.room.externalWall.windowType === "2") return true;
				return this.room.externalWall.windowArea === null || this.room.externalWall.windowArea === "" || parseFloat(this.room.externalWall.windowArea) <= 0 ? false : true;
			},
			windowTypeIsValid: function() {
				return this.room.externalWall.windowType === null || this.room.externalWall.windowType === "" ? false : true;
			},
			wallLengthIsValid: function() {
				return this.room.externalWall.wallLength === null || this.room.externalWall.wallLength === "" || parseFloat(this.room.externalWall.wallLength) <= 0 ? false : true;
			},
			wallTypeIsValid: function() {
				return this.room.externalWall.wallType === null || this.room.externalWall.wallType === "" ? false : true;
			},
			floorAreaIsValid: function() {
				return this.room.floorArea === null || this.room.floorArea === "" || parseFloat(this.room.floorArea) <= 0 ? false : true;
			},
			roomHeightIsValid: function() {
				return this.room.roomHeight === null || this.room.roomHeight === "" ? false : true;
			},
			floorLevelIsValid: function() {
				return this.room.floorLevel === null || this.room.floorLevel === "" ? false : true;
			},
			floorsAreValid: function() {
				if (!this.floorLevelIsValid) return false;
				if (!this.roomHeightIsValid) return false;
				if (!this.floorAreaIsValid) return false;
				return true;
			},
			lengthSymbol: function() {
				return this.lengthUnitTypes.filter(unit => {
  				return unit.handle === this.activeUnit
				})[0].symbol;
			},
			areaSymbol: function() {
				return this.areaUnitTypes.filter(unit => {
					return unit.handle === this.activeUnit
				})[0].symbol;
			},
			volumeSymbol: function() {
				return this.volumeUnitTypes.filter(unit => {
					return unit.handle === this.activeUnit
				})[0].symbol;
			},
			possibleRoomHeights: function() {
				return this.roomHeights.filter(unit => {
					return unit.type === this.activeUnit
				});
			},
			possibleConservatoryHeights: function() {
				return this.conservatoryHeights.filter(unit => {
					return unit.type === this.activeUnit
				});
			},
			roomStep1: function() {
				if (!this.room.floorLevel || !this.room.roomHeight || !this.room.floorArea) return 0;
				let floorId = this.floorTypeValue(this.room.floorLevel);
				let heightId = this.room.roomHeight;
				let multiplier = this.floorTotal(floorId + heightId);
				let floorArea = this.room.floorArea;
				let subtotal = floorArea * multiplier[this.activeUnit];
				if (this.room.variation.singleStoryBuilding) subtotal = subtotal * 1.1;
				if (this.room.variation.ceilingWithoutInsulationOnTopFloorsAndSingleStoryBuildings) subtotal = subtotal * 2;
				if (this.room.variation.floorWithInsulation) subtotal = subtotal * 0.78;
				return subtotal;
			},
			roomStep2: function() {
				if (!this.room.externalWalls.length) return 0;
				let subtotal = 0;
				let height = this.room.roomHeight;
				this.room.externalWalls.forEach(wall => {
					let multiplier = this.wallTotal(wall.wallType + height);
					subtotal = subtotal + (multiplier[this.activeUnit] * wall.wallLength);
				});
				return subtotal;
			},
			roomStep3: function() {
				if (!this.room.externalWalls.length) return 0;
				let subtotal = 0;
				this.room.externalWalls.filter(window => window.windowType !== "2").forEach(window => {
					let multiplier = this.room.variation.allWindowWalls ? this.allWindowTotal(window.windowType) : this.windowTotal(window.windowType + window.wallType);
					subtotal = subtotal + (multiplier[this.activeUnit] * window.windowArea);
				});
				return subtotal;
			},
			roomStep4: function() {
				if (!this.room.skylights.length) return 0;
				let subtotal = 0;
				this.room.skylights.filter(skylight => skylight.skylightType !== "2").forEach(skylight => {
					let multiplier = this.skylightTotal(skylight.skylightType);
					subtotal = subtotal + (multiplier[this.activeUnit] * skylight.skylightArea);
				});
				return subtotal;
			},
			roomCalculatedValue: function() {
				let step1 = this.roomStep1;
				let step2 = this.roomStep2;
				let step3 = this.roomStep3;
				let baseTotal = this.room.variation.internalRoomWithFanExtractor ? step1 * 2 : 0;
				baseTotal = baseTotal === 0 && this.room.variation.allWindowWalls ? step1 + step3 : baseTotal;
				baseTotal = baseTotal === 0 ? step1 + step2 + step3 : baseTotal;
				baseTotal = this.room.variation.coolerRoom ? baseTotal * 0.75 : baseTotal;
				baseTotal = this.room.variation.exposedSituations ? baseTotal * 1.2 : baseTotal;
				return this.room.variation.veluxWindowsOrSkylights ? baseTotal + this.roomStep4 : baseTotal;
			},
			conservatoryStep1: function() {
				let multiplier = this.conservatoryFloorTotal(this.conservatory.type + this.conservatory.roomHeight);
				return multiplier[this.activeUnit] * this.conservatory.floorArea;
			},
			conservatoryStep2: function() {
				let multiplier = this.conservatoryWallTotal(this.conservatory.type + this.conservatory.roomHeight);
				return multiplier[this.activeUnit] * this.conservatory.lengthOfWalls;
			},
			conservatoryCalculatedValue: function() {
				let step1 = this.conservatoryStep1;
				let step2 = this.conservatoryStep2;
				let baseTotal = step1 + step2;
				return this.conservatory.variation.forBackgroundHeatOnly ? baseTotal * 0.6 : baseTotal;
			},
		},
		mounted: function() {
			this.setActiveCalculator(this.defaultCalculator());
		},
		created: function() {
			this.setActiveUnit(this.defaultUnit());
		},
	};
</script>

<style scoped>
	.step {
		border-bottom: 1px solid #b8b8b8;
		margin: 1rem 0;
		padding: 0 0 1rem;
	}

	.fields {
		display: flex;
		justify-content: flex-start;
		flex-wrap: wrap;
	}

	.field {
		display: flex;
		width: 100%;
		border: 2px solid #b8b8b8;
		border-radius: 3px;
		margin: 0 5px 1rem 0;
	}

	.inline {
		display: inline-flex;
		min-width: 200px;
	}

	.field:focus-within {
		border: 2px solid #000;
	}

	.field.error {
		border: 2px solid #e30613;
	}

	input,
	select {
		width: 100%;
		border: none;
		outline: none;
	}

	input {
		text-align: right;
		padding: 0.75rem 0;
	}

	span {
		padding: 0 0.75rem 0 0;
		margin: auto 0;
		color: #858585;
	}

	button {
		background: #2b2012;
		border: 2px solid #2b2012;
		color: #fff;
		margin: 0 5px 1rem 0;
		border-radius: 3px;
		outline: none;
	}

	button:hover,
	button:focus {
		background: none;
		color: #2b2012;
		cursor: pointer;
	}

	.checkboxes {
		flex-wrap: wrap;
	}

	.checkbox-label {
		width: 100%;
		text-align: center;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		padding: 1rem;
	}

	.checkbox-label span {
		width: 80%;
	}

	.checkbox-label input {
		width: 20%;
		transform: scale(1.5);
	}

	.total {
		display: inline-block;
	}

	.calculate {
		padding: 0.75rem;
		width: 50%;
	}

	.errors li {
		color: #e30613;
		font-style: italic;
	}

	@media screen and (min-width: 640px) {
		.field {
			width: calc(50% - 5px);
		}

		.calculate {
			width: 20%;
		}
	}

	@media screen and (min-width: 1280px) {
		.fields {
			flex-wrap: nowrap;
		}

		.checkboxes {
			flex-wrap: wrap;
		}

		.field {
			width: 20%;
		}
	}
</style>
